/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 컴포넌트 공통 클래스를 정의한다.
 *
 * @file /src/base/Component.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 10. 15.
 */
import * as parent from '@base/Base';
import { Viewport } from '@base/Viewport';
import { Components } from '@base/Components';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Connector } from '@utils/Connector';
import { Renderer } from '@utils/Renderer';
import { Scroller } from '@utils/Scroller';
import { Exceptions } from '@utils/Exceptions';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 컴포넌트가 조립되기 전
     */
    beforeRender?: (me: T) => void | Promise<void>;

    /**
     * 컴포넌트가 렌더링 되었을 때
     */
    render?: (me: T) => void | Promise<void>;

    /**
     * 컴포넌트가 제거되었을 때
     */
    remove?: (me: T) => void | Promise<void>;

    /**
     * 컴포넌트 크기가 변경되었을 때
     */
    resize?: (me: T) => void | Promise<void>;

    /**
     * 컴포넌트에 키보드 입력이 발생하였을 때
     */
    keyboard?: (e: KeyboardEvent, me: T) => void | Promise<void>;

    /**
     * 컴포넌트가 포커스될 때
     */
    focus?: (me: T) => void | Promise<void>;

    /**
     * 컴포넌트가 포커스 해제될 때
     */
    blur?: (me: T) => void | Promise<void>;

    /**
     * 컴포넌트가 보여질 떄
     */
    show?: (me: T) => void;

    /**
     * 컴포넌트가 숨겨질 떄
     */
    hide?: (me: T) => void;

    /**
     * 자식 컴포넌트가 추가되었을 때
     */
    appendItem?: (item: Component, me: T) => void | Promise<void>;

    /**
     * 자식 컴포넌트가 제거되었을 때
     */
    removeItem?: (item: Component, me: T) => void | Promise<void>;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 컴포넌트 고유값 (정의되어 있지 않은 경우 자동으로 부여한다.)
     */
    id?: string;

    /**
     * 컴포넌트 명 (같은 부모 컴포넌트내에선 유일하여야 합니다.)
     */
    name?: string;

    /**
     * 컴포넌트 레이아웃
     */
    layout?: 'auto' | 'fit' | 'column' | 'row';

    /**
     * 컴포넌트의 하위 컴포넌트
     */
    items?: Component[];

    /**
     * 컴포넌트 가변너비
     */
    flex?: boolean | number;

    /**
     * 컴포넌트 너비(px)
     */
    width?: number;

    /**
     * 컴포넌트 너비(rem)
     */
    length?: number;

    /**
     * 컴포넌트 높이(px)
     */
    height?: number;

    /**
     * 컴포넌트 최대너비
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    maxWidth?: number | string;

    /**
     * 컴포넌트 최대높이
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    maxHeight?: number | string;

    /**
     * 컴포넌트 최소너비
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    minWidth?: number | string;

    /**
     * 컴포넌트 최소높이
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    minHeight?: number | string;

    /**
     * 컴포넌트 내부 여백(px)
     * - 단일값
     * - 배열 : [세로방향, 가로방향] 또는 [위, 오른쪽, 아래, 왼쪽] 또는 [위, 가로방향, 아래]
     */
    padding?: number | number[];

    /**
     * 컴포넌트 내부 여백(rem)
     * - 단일값
     * - 배열 : [세로방향, 가로방향] 또는 [위, 오른쪽, 아래, 왼쪽] 또는 [위, 가로방향, 아래]
     */
    paddingLength?: number | number[];

    /**
     * 컴포넌트 외부 여백(px)
     * 외부 여백처럼 설정하지만, DOM 의 크기계산의 일관성을 위하여 최상위 DOM 의 padding 으로 처리
     * - 단일값
     * - 배열 : [세로방향, 가로방향] 또는 [위, 오른쪽, 아래, 왼쪽] 또는 [위, 가로방향, 아래]
     */
    margin?: number | number[];

    /**
     * 컴포넌트 외부 여백(rem)
     * 외부 여백처럼 설정하지만, DOM 의 크기계산의 일관성을 위하여 최상위 DOM 의 padding 으로 처리
     * - 단일값
     * - 배열 : [세로방향, 가로방향] 또는 [위, 오른쪽, 아래, 왼쪽] 또는 [위, 가로방향, 아래]
     */
    marginLength?: number | number[];

    /**
     * 컴포넌트 스타일정의
     */
    style?: Partial<Omit<CSSStyleDeclaration, 'width' | 'height' | 'padding' | 'margin'>>;

    /**
     * 컴포넌트에 적용할 사용자 정의 CSS 클래스
     */
    className?: string | string[];

    /**
     * 테두리여부
     * 배열인 경우 위, 우측, 아래, 좌측
     */
    border?: boolean | [boolean, boolean, boolean, boolean];

    /**
     * 컴포넌트 스크롤여부
     */
    scrollable?: 'x' | 'y' | boolean;

    /**
     * 키보드 이벤트 처리여부 (true 일 경우 onKeyboard() 함수에 keydown 이벤트가 전파된다.)
     */
    keyboard?: boolean;

    /**
     * 컴포넌트 숨김여부
     */
    hidden?: boolean;

    /**
     * 컴포넌트 비활성화여부
     */
    disabled?: boolean;

    /**
     * 드래그 허용 여부
     */
    dragable?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export abstract class Component extends parent.Base {
    /**
     * 컴포넌트 타입
     * 하위 클래스에서 직접 타입을 지정하고, 해당 타입은 컴포넌트 DOM 에 추가된다.
     */
    abstract type: string;

    /**
     * 컴포넌트 확장타입
     * 특정 컴포넌트의 하위 컴포넌트로써 기능이 확장된 컴포넌트일 경우 사용한다.
     */
    abstract variant: string;

    /**
     * 컴포넌트 고유값 (정의되어 있지 않은 경우 자동으로 부여한다.)
     */
    id: string;

    /**
     * 컴포넌트 명 (같은 부모 컴포넌트내에선 유일하여야 합니다.)
     */
    name: string | null;

    /**
     * 뷰포트
     */
    viewport: Viewport;

    /**
     * 부모 컴포넌트
     */
    parent: Component | null = null;

    /**
     * 컴포넌트 레이아웃
     */
    layout: 'auto' | 'fit' | 'column' | 'row';

    /**
     * 컴포넌트의 하위 컴포넌트
     */
    items: Component[] = null;

    /**
     * 컴포넌트 가변너비
     */
    flex: boolean | number | null;

    /**
     * 컴포넌트 너비(px)
     */
    width: number | null;

    /**
     * 컴포넌트 너비(rem)
     */
    length: number | null;

    /**
     * 컴포넌트 높이(px)
     */
    height: number | null;

    /**
     * 컴포넌트 최대너비
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    maxWidth: number | string | null;

    /**
     * 컴포넌트 최대높이
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    maxHeight: number | string | null;

    /**
     * 컴포넌트 최소너비
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    minWidth: number | string | null;

    /**
     * 컴포넌트 최소높이
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    minHeight: number | string | null;

    /**
     * 컴포넌트 내부 여백(px)
     * - 단일값
     * - 배열 : [세로방향, 가로방향] 또는 [위, 오른쪽, 아래, 왼쪽] 또는 [위, 가로방향, 아래]
     */
    padding: number | number[] | null;

    /**
     * 컴포넌트 내부 여백(rem)
     * - 단일값
     * - 배열 : [세로방향, 가로방향] 또는 [위, 오른쪽, 아래, 왼쪽] 또는 [위, 가로방향, 아래]
     */
    paddingLength: number | number[] | null;

    /**
     * 컴포넌트 외부 여백(px)
     * 외부 여백처럼 설정하지만, DOM 의 크기계산의 일관성을 위하여 최상위 DOM 의 padding 으로 처리
     * - 단일값
     * - 배열 : [세로방향, 가로방향] 또는 [위, 오른쪽, 아래, 왼쪽] 또는 [위, 가로방향, 아래]
     */
    margin: number | number[] | null;

    /**
     * 컴포넌트 외부 여백(rem)
     * 외부 여백처럼 설정하지만, DOM 의 크기계산의 일관성을 위하여 최상위 DOM 의 padding 으로 처리
     * - 단일값
     * - 배열 : [세로방향, 가로방향] 또는 [위, 오른쪽, 아래, 왼쪽] 또는 [위, 가로방향, 아래]
     */
    marginLength: number | number[] | null;

    /**
     * 컴포넌트 스타일정의
     */
    style: Partial<Omit<CSSStyleDeclaration, 'width' | 'height' | 'padding' | 'margin'>> | null;

    /**
     * 컴포넌트에 적용할 사용자 정의 CSS 클래스
     */
    className: string | string[] | null;

    /**
     * 테두리여부
     * 배열인 경우 위, 우측, 아래, 좌측
     */
    border: boolean | [boolean, boolean, boolean, boolean] = false;

    /**
     * 컴포넌트 스크롤여부
     */
    scrollable: 'x' | 'y' | boolean = false;

    /**
     * 키보드 이벤트 처리여부 (true 일 경우 onKeyboard() 함수에 keydown 이벤트가 전파된다.)
     */
    keyboard: boolean;

    /**
     * 컴포넌트 숨김여부
     */
    hidden: boolean = false;

    /**
     * 컴포넌트 비활성화여부
     */
    disabled: boolean = false;

    /**
     * 드래그 허용 여부
     */
    dragable: boolean = false;

    /**
     * 컴포넌트 DOM 연결 여부
     */
    connected: boolean = false;

    /**
     * 컴포넌트가 렌더링중인지 여부
     */
    rendering: Promise<void> = null;

    /**
     * 컴포넌트 렌더링여부
     */
    rendered: boolean = false;

    /**
     * 컴포넌트 최상위 DOM
     */
    $component: Dom = null;

    /**
     * 컴포넌트 컨테이너 DOM
     */
    $container: Dom = null;

    /**
     * 컴포넌트 콘텐츠 DOM
     */
    $content: Dom = null;

    /**
     * 컴포넌트 내부 스크롤되는 DOM 객체
     */
    $scrollable: Dom = null;

    /**
     * 파일이 드랍되는 DOM 객체
     */
    $droppable: Dom = null;

    /**
     * 스크롤러
     */
    scroller: Scroller;

    /**
     * 컴포넌트 영역의 크기가 변경되는 것을 감지하는 옵저버
     */
    private static observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
            const component = Components.get(entry.target.getAttribute('data-component'));
            if (component.isRendered() == true) {
                component.getScroller()?.updatePosition();
                for (const parent of component.getScrollableParents()) {
                    if (parent.isRendered() == true) {
                        parent.getScroller().updatePosition();
                    }
                }
                component.fireEvent('resize', [component]);
            }
        }
    });

    /**
     * 컴포넌트 영역의 크기가 변경되는 것을 감지중인지 여부
     */
    isObserved: boolean = false;

    /**
     * 기본클래스를 정의한다.
     *
     * @param properties - 컴포넌트 초기속성
     */
    constructor(public properties: Properties<Component> = null) {
        super(properties);

        this.id = this.properties?.id ?? Components.createId();
        this.name = this.properties?.name ?? null;
        this.layout = this.properties?.layout ?? 'auto';
        this.items = this.properties?.items ?? [];
        this.flex = this.properties?.flex ?? null;
        this.width = this.properties?.width ?? null;
        this.length = this.properties?.length ?? null;
        this.height = this.properties?.height ?? null;
        this.maxWidth = this.properties?.maxWidth ?? null;
        this.maxHeight = this.properties?.maxHeight ?? null;
        this.minWidth = this.properties?.minWidth ?? null;
        this.minHeight = this.properties?.minHeight ?? null;
        this.padding = this.properties?.padding ?? null;
        this.paddingLength = this.properties?.paddingLength ?? null;
        this.margin = this.properties?.margin ?? null;
        this.marginLength = this.properties?.marginLength ?? null;
        this.style = this.properties?.style ?? null;
        this.className = this.properties?.className ?? null;
        this.border = this.properties?.border ?? false;
        this.scrollable = this.properties?.scrollable ?? false;
        if (this.scrollable !== false) {
            this.$scrollable = this.$scrollable ?? this.$getContainer();
        }
        this.keyboard = this.properties?.keyboard === true;
        this.hidden = this.properties?.hidden ?? false;
        this.disabled = this.properties?.disabled ?? false;
        this.dragable = this.properties?.dragable ?? false;
        this.$droppable = null;

        queueMicrotask(this.initItems.bind(this));
        Components.set(this);
    }

    /**
     * 자식 컴포넌트를 초기화한다.
     */
    initItems(): void {
        this.items = (this.items ?? []).filter((item) => item !== null);
        for (const item of this.items) {
            item.setParent(this);
        }
    }

    /**
     * 컴포넌트의 고유값을 가져온다.
     *
     * @returns 고유값
     */
    getId(): string {
        return this.id;
    }

    /**
     * 컴포넌트의 이름을 가져온다.
     *
     * @returns 이름
     */
    getName(): string | null {
        return this.name;
    }

    /**
     * 컴포넌트의 이름을 설정한다.
     *
     * @param name - 이름
     */
    setName(name: string): void {
        this.name = name;
        if (this.$component !== null) {
            this.$component.setAttr('data-name', this.name);
        }
    }

    /**
     * 뷰포트를 가져온다.
     *
     * @returns 뷰포트
     */
    getViewport(): Viewport {
        return this.viewport ?? this.parent?.getViewport() ?? null;
    }

    /**
     * 부모 컴포넌트를 가져온다.
     *
     * @returns 부모 컴포넌트
     */
    getParent<T extends Component = Component>(): T | null {
        return (this.parent as T) ?? null;
    }

    /**
     * 특정 타입의 조상 컴포넌트를 가져온다.
     *
     * @param component - 찾을 컴포넌트 클래스
     * @returns 조상 컴포넌트
     */
    getParents<T extends Component>(component: abstract new (...args: any[]) => T): T | null {
        let parent: Component = this;
        while (parent !== null) {
            if (parent instanceof component) {
                return parent as T;
            }

            parent = parent.getParent();
        }

        return null;
    }

    /**
     * 이름을 통해 부모 컴포넌트를 가져온다.
     *
     * @param name - 가져올 명
     * @returns 부모 컴포넌트
     */
    getParentByName<T extends Component = Component>(name: string): T | null {
        let parent = this.getParent();
        while (parent !== null) {
            if (parent.getName() === name) {
                return parent as T;
            }

            if (parent instanceof Component) {
                parent = parent.getParent();
            } else {
                parent = null;
            }
        }

        return null;
    }

    /**
     * 뷰포트를 지정한다.
     *
     * @param viewport - 뷰포트
     */
    setViewport(viewport: Viewport): void {
        this.viewport = viewport;
    }

    /**
     * 부모 컴포넌트를 지정한다.
     *
     * @param parent - 부모 컴포넌트
     */
    setParent(parent: Component | null): void {
        this.parent = parent;
    }

    /**
     * 레이아웃을 설정한다.
     *
     * @param layout - 레이아웃 설정
     */
    setLayout(layout: 'auto' | 'fit' | 'column' | 'row'): void {
        this.layout = layout;
        this.$getComponent().setAttr('data-layout', this.layout);
    }

    /**
     * 레이아웃 설정을 가져온다.
     *
     * @returns 레이아웃 설정
     */
    getLayout(): 'auto' | 'fit' | 'column' | 'row' {
        return this.layout;
    }

    /**
     * 자식 컴포넌트를 추가한다.
     *
     * @param item - 추가할 컴포넌트
     * @param index - 추가할 위치 (정의되지 않은 경우 제일 마지막에 추가한다)
     */
    async appendItem(item: Component, index: number | undefined = undefined): Promise<void> {
        this.items ??= [];
        item.setParent(this);

        const count = this.items.length;

        if (index === undefined || index >= count) {
            this.items.push(item);
        } else if (index < 0 && Math.abs(index) >= count) {
            this.items.unshift(item);
        } else {
            this.items.splice(index, 0, item);
        }

        /**
         * 현재 컴포넌트가 렌더링되어 있는 경우,
         * 추가한 컴포넌트를 바로 렌더링 한다.
         */
        if (this.isRendered() == true) {
            this.$getContent().append(item.$getComponent(), index);

            await Renderer.request(() => item.render());
        }

        await this.onAppendItem(item);
    }

    /**
     * 자식 컴포넌트를 제거한다.
     *
     * @param component - 제거할 자식 컴포넌트
     * @param removeDom - 자식의 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async removeItem(item: Component, removeDom: boolean = true): Promise<void> {
        const index = this.items.findIndex((child) => child.getId() == item.getId());
        if (index !== -1) {
            if (removeDom == true) {
                item.$getComponent().remove();
                await item.remove(!removeDom);
            }

            this.items.splice(index, 1);

            await this.onRemoveItem(item);
        }
    }

    /**
     * 모든 자식 컴포넌트를 가져온다.
     *
     * @returns 모든 자식 컴포넌트
     */
    getItems(): Component[] {
        return this.items ?? [];
    }

    /**
     * 인덱스를 통해 자식 컴포넌트를 가져온다.
     *
     * @param index - 가져올 컴포넌트 인덱스
     * @returns 자식 컴포넌트
     */
    getItemAt<T extends Component = Component>(index: number): T | null {
        return (this.items.at(index) as T) ?? null;
    }

    /**
     * 이름을 통해 자식 컴포넌트를 가져온다.
     *
     * @param name - 가져올 명
     * @returns 자식 컴포넌트
     */
    getItemByName<T extends Component = Component>(name: string): T | null {
        const queue: Component[] = [this];
        while (queue.length > 0) {
            const current = queue.shift()!;
            for (const item of current.items ?? []) {
                if (item.getName() === name) {
                    return item as T;
                }
                if (item instanceof Component) {
                    queue.push(item);
                }
            }
        }
        return null;
    }

    /**
     * 컴포넌트 최상위 DOM 을 가져온다.
     *
     * @returns 최상위 DOM
     */
    $getComponent(): Dom {
        if (this.$component === null) {
            const $dom = document.createElement('aui-component') as AuiComponent;
            $dom.setComponent(this);
            this.$component = Html.el($dom);
            this.$component.setAttr('data-component', this.id);
            this.$component.setAttr('data-type', this.type);
            this.$component.setAttr('data-variant', this.variant);
            this.$component.setAttr('data-layout', this.layout);

            if (this.name !== null) {
                this.$component.setAttr('data-name', this.name);
            }

            if (this.className !== null) {
                this.$component.addClass(...(Array.isArray(this.className) ? this.className : [this.className]));
            }
        }

        return this.$component;
    }

    /**
     * 컴포넌트 컨테이너 DOM 을 가져온다.
     *
     * @returns 컨테이너 DOM
     */
    $getContainer(): Dom {
        if (this.$container === null) {
            this.$container = Html.create('div');
            this.$container.setAttr('data-role', 'container');
        }

        return this.$container;
    }

    /**
     * 컴포넌트 콘텐츠 DOM 을 가져온다.
     * 컴포넌트 종류에 따라 콘텐츠 DOM 타입변경이 필요할 경우, 하위 클래스에서 재정의한다.
     *
     * @returns 콘텐츠 DOM
     */
    $getContent(): Dom {
        if (this.$content === null) {
            this.$content = Html.create('div');
            this.$content.setAttr('data-role', 'content');
        }

        return this.$content;
    }

    /**
     * 컴포넌트 너비(px)를 설정한다.
     *
     * @param width - 너비
     */
    setWidth(width: number): void {
        if (width === null) {
            this.width = null;
            this.$getComponent().setStyle('width', null);
            if (this.length !== null) {
                this.setLength(this.length);
            }
            return;
        }

        this.width = width;
        this.$getComponent().setStyle('width', this.width + 'px');

        if (this.layout == 'row') {
            this.$getComponent().setStyle('flex', null);
        }
    }

    /**
     * 컴포넌트 너비(rem)를 설정한다.
     *
     * @param length - 너비
     */
    setLength(length: number): void {
        if (length === null) {
            this.length = null;
            this.$getComponent().setStyle('width', null);
            if (this.width !== null) {
                this.setWidth(this.width);
            }
            return;
        }

        this.length = length;
        this.$getComponent().setStyle('width', this.length + 'rem');

        if (this.layout == 'row') {
            this.$getComponent().setStyle('flex', null);
        }
    }

    /**
     * 컴포넌트 높이(px)를 설정한다.
     *
     * @param height - 높이
     */
    setHeight(height: number): void {
        if (height === null) {
            this.height = null;
            this.$getComponent().setStyle('height', null);
            return;
        }

        this.height = height;
        this.$getComponent().setStyle('height', this.height + 'px');

        if (this.layout == 'column') {
            this.$getComponent().setStyle('flex', null);
        }
    }

    /**
     * 컴포넌트 flex 를 설정한다.
     *
     * @param flex - 비율
     */
    setFlex(flex: number | boolean): void {
        if (flex === null || flex === false) {
            this.flex = null;
            this.$getComponent().setStyle('flex', null);
            if (this.width !== null) {
                this.setWidth(this.width);
            } else if (this.length !== null) {
                this.setWidth(this.length);
            }
            return;
        }

        if (this.layout == 'row' || this.layout == 'column') {
            this.flex = flex;
            this.$getComponent().setStyle('flex', flex === true ? '1' : flex.toString());
        }

        if (this.layout == 'row') {
            this.$getComponent().setStyle('height', null);
        }

        if (this.layout == 'column') {
            this.$getComponent().setStyle('width', null);
        }
    }

    /**
     * 컴포넌트 최대너비(px)를 설정한다.
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     *
     * @param maxWidth - 최대너비
     */
    setMaxWidth(maxWidth: string | number): void {
        if (maxWidth === null) {
            this.maxWidth = null;
            this.$getComponent().setStyle('max-width', null);
            return;
        }

        this.maxWidth = typeof maxWidth == 'number' ? maxWidth + 'px' : maxWidth;
        this.$getComponent().setStyle('max-width', this.maxWidth);
    }

    /**
     * 컴포넌트 최대높이(px)를 설정한다.
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     *
     * @param maxHeight - 최대높이
     */
    setMaxHeight(maxHeight: string | number): void {
        if (maxHeight === null) {
            this.maxHeight = null;
            this.$getComponent().setStyle('max-height', null);
            return;
        }

        this.maxHeight = typeof maxHeight == 'number' ? maxHeight + 'px' : maxHeight;
        this.$getComponent().setStyle('max-height', this.maxHeight);
    }

    /**
     * 컴포넌트 최소너비를 설정한다.
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     *
     * @param minWidth - 최소너비
     */
    setMinWidth(minWidth: string | number): void {
        if (minWidth === null) {
            this.minWidth = null;
            this.$getComponent().setStyle('min-width', null);
            return;
        }

        this.minWidth = typeof minWidth == 'number' ? minWidth + 'px' : minWidth;
        this.$getComponent().setStyle('min-width', this.minWidth);
    }

    /**
     * 컴포넌트 최소높이를 설정한다.
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     *
     * @param minHeight - 최소높이
     */
    setMinHeight(minHeight: string | number): void {
        if (minHeight === null) {
            this.minHeight = null;
            this.$getComponent().setStyle('min-height', null);
            return;
        }

        this.minHeight = typeof minHeight == 'number' ? minHeight + 'px' : minHeight;
        this.$getComponent().setStyle('min-height', this.minHeight);
    }

    /**
     * 컴포넌트 숨김여부를 설정한다.
     *
     * @param hidden - 숨김여부
     */
    async setHidden(hidden: boolean): Promise<void> {
        if (this.hidden == hidden) {
            return;
        }

        this.hidden = hidden;

        if (this.hidden == true) {
            this.$getComponent().hide();
            this.fireEvent('hide', [this]);
        } else {
            this.$getComponent().show();

            /**
             * 컴포넌트가 렌더링이 필요한 경우, 렌더링한다.
             */
            await this.render();

            /**
             * 컴포넌트의 지연연결이 필요한 경우, 연결하고 connect 이벤트를 발생시킨다.
             */
            if (Connector.hasStored(this.$getComponent().getEl()) == true) {
                await this.onConnected();
            } else {
                this.getScroller()?.updatePosition();
            }

            this.fireEvent('show', [this]);
        }
    }

    /**
     * 컴포넌트를 보인다.
     */
    async show(): Promise<void> {
        await this.setHidden(false);
    }

    /**
     * 컴포넌트를 숨긴다.
     */
    async hide(): Promise<void> {
        await this.setHidden(true);
    }

    /**
     * 키보드 이벤트 수신여부를 가져온다.
     *
     * @returns 키보드 이벤트 수신여부
     */
    isKeyboard(): boolean {
        return this.keyboard == true || this.hasEvent('keyboard') == true;
    }

    /**
     * 컴포넌트의 보임여부를 가져온다.
     *
     * @returns 보임여부
     */
    isShow(): boolean {
        return this.isRendered() == true && this.isHidden() == false;
    }

    /**
     * 컴포넌트 숨김여부를 가져온다.
     *
     * @returns 숨김여부
     */
    isHidden(): boolean {
        return this.hidden;
    }

    /**
     * 컴포넌트를 비활성화한다.
     */
    disable(): void {
        this.setDisabled(true);
    }

    /**
     * 컴포넌트를 활성화한다.
     */
    enable(): void {
        this.setDisabled(false);
    }

    /**
     * 컴포넌트에 포커스를 지정한다.
     */
    focus(): void {
        this.$getComponent().focus();
    }

    /**
     * 컴포넌트에 포커스를 해제한다.
     */
    blur(): void {
        this.$getComponent().blur();
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.disabled = disabled;
        return this;
    }

    /**
     * 컴포넌트의 비활성여부를 가져온다.
     *
     * @returns 비활성화 여부
     */
    isDisabled(): boolean {
        return this.disabled;
    }

    /**
     * 컴포넌트가 렌더링되었는지 확인한다.
     *
     * @returns 렌더링여부
     */
    isRendered(): boolean {
        return this.rendered;
    }

    /**
     * 컴포넌트가 연결되었고, 지연연결 DOM 연결이 완료되었는지 여부를 가져온다.
     *
     * @returns 컴포넌트 연결여부
     */
    isConnected(): boolean {
        return this.$getComponent().isConnected() == true && Connector.hasStored(this.$getComponent().getEl()) == false;
    }

    /**
     * 컴포넌트 내부에 포커스를 가진 DOM 이 존재하는지 확인한다.
     *
     * @returns 포커스존재여부
     */
    isFocused(): boolean {
        return this.isRendered() == true && this.$getComponent().getEl().contains(document.activeElement);
    }

    /**
     * 컴포넌트가 렌더링이 가능한지 확인한다.
     * 부모 컴포넌트가 존재할 경우, 부모 컴포넌트가 보이는 상태이고,
     * 현재 컴포넌트가 보이는 상태이며, 렌더링 되지 않은 경우를 의미한다.
     *
     * @returns 렌더링 가능여부
     */
    isRenderable(): boolean {
        if (this.isRendered() == false && this.isHidden() == true) {
            this.$getComponent().hide();
        }

        if (this.isRendered() == true) {
            return false;
        }

        if (this.getParent()?.$getContent().isConnected() === false) {
            return false;
        }

        if (this.isHidden() == true) {
            return false;
        }

        return true;
    }

    /**
     * DOM 요소에 여백을 적용한다.
     *
     * @param target - 적용할 DOM
     * @param padding - 여백설정
     * @param unit - 단위
     */
    protected applyPadding(target: HTMLElement, padding: number | number[], unit: 'px' | 'rem') {
        if (Array.isArray(padding)) {
            target.style.padding = padding.map((padding) => padding + unit).join(' ');
        } else {
            target.style.padding = padding + unit;
        }
    }

    /**
     * 스크롤 Element를 가져온다.
     *
     * @returns 스크롤 Element (스크롤 되지 않는 컴포넌트인 경우 NULL)
     */
    getScroller(): Scroller {
        if (this.scroller == undefined) {
            if (this.scrollable === false) {
                this.scroller = null;
            } else {
                this.scroller = Scroller.get({
                    $target: this.$scrollable,
                    $component: this.$component,
                    scrollable: this.scrollable,
                });
            }
        }

        return this.scroller;
    }

    /**
     * 스크롤이 가능한 부모 컴포넌트를 가져온다.
     *
     * @returns 스크롤이 가능한 부모 컴포넌트
     */
    getScrollableParents(): Component[] {
        const scrollableParents: Component[] = [];
        let parent: Component | null = this.getParent();
        while (parent !== null) {
            if (parent.scrollable !== false) {
                scrollableParents.push(parent);
            }

            parent = parent.getParent();
        }

        return scrollableParents;
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        if (this.$getContainer().isConnected() == false) {
            this.$getComponent().append(this.$getContainer());
        }

        this.$getComponent().setAttr('tabindex', '-1');
        this.$getComponent().setStyle('anchor-name', '--aui-component-' + this.id);

        if (this.padding !== null) {
            this.applyPadding(this.$getContent().getEl(), this.padding, 'px');
        } else if (this.paddingLength !== null) {
            this.applyPadding(this.$getContent().getEl(), this.paddingLength, 'rem');
        }

        if (this.margin !== null) {
            this.applyPadding(this.$getComponent().getEl(), this.margin, 'px');
        } else if (this.marginLength !== null) {
            this.applyPadding(this.$getComponent().getEl(), this.marginLength, 'rem');
        }

        if (this.border === true) {
            this.$getComponent().addClass('border');
        } else if (Array.isArray(this.border) == true) {
            const positions = ['Top', 'Right', 'Bottom', 'Left'];
            this.border.forEach((border, position) => {
                if (border == true) {
                    this.$getComponent().addClass('border' + positions[position]);
                }
            });
        }

        if (this.getParent()?.getLayout() == 'row' && this.flex !== null) {
            if (this.flex !== null) {
                this.$getComponent().setStyle('flex', this.flex === true ? '1' : this.flex.toString());
            } else {
                this.$getComponent().setStyle('flex-shrink', '0');
            }
        } else if (this.width !== null) {
            this.$getComponent().setStyle('width', this.width + 'px');
        } else if (this.length !== null) {
            this.$getComponent().setStyle('width', this.length + 'rem');
        }

        if (this.getParent()?.getLayout() == 'column') {
            if (this.flex !== null) {
                this.$getComponent().setStyle('flex', this.flex === true ? '1' : this.flex.toString());
            } else {
                this.$getComponent().setStyle('flex-shrink', '0');
            }
        } else if (this.height !== null) {
            this.$getComponent().setStyle('height', this.height + 'px');
        }

        if (this.maxWidth !== null) {
            this.maxWidth = typeof this.maxWidth == 'number' ? this.maxWidth + 'px' : this.maxWidth;
            this.$getComponent().setStyle('max-width', this.maxWidth);
        }

        if (this.maxHeight !== null) {
            this.maxHeight = typeof this.maxHeight == 'number' ? this.maxHeight + 'px' : this.maxHeight;
            this.$getComponent().setStyle('max-height', this.maxHeight);
        }

        if (this.minWidth !== null) {
            this.minWidth = typeof this.minWidth == 'number' ? this.minWidth + 'px' : this.minWidth;
            this.$getComponent().setStyle('min-width', this.minWidth);
        }

        if (this.minHeight !== null) {
            this.minHeight = typeof this.minHeight == 'number' ? this.minHeight + 'px' : this.minHeight;
            this.$getComponent().setStyle('min-height', this.minHeight);
        }

        this.connected = true;
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     * 내부 콘텐츠 렌더링이 복잡할 경우, 하위 클래스에서 재정의하여 사용한다.
     *
     * @param is_force - 렌더링이 당장 필요없는 상태라도, 강제로 렌더링할지 여부
     */
    async renderContainer(is_force: boolean = false): Promise<void> {
        this.$getContainer().append(this.$getContent());
        await this.renderItems(is_force);
    }

    /**
     * 컴포넌트 자식 컴포넌트를 렌더링한다.
     * 하위 클래스에서 재정의되지 않을 경우, 기본적으로 items 에 정의된 자식 컴포넌트를 렌더링한다.
     *
     * @param is_force - 렌더링이 당장 필요없는 상태라도, 강제로 렌더링할지 여부
     */
    async renderItems(is_force: boolean = false): Promise<void> {
        /**
         * 콘텐츠 DOM 에 자식 컴포넌트의 DOM 을 추가하고, 자식 컴포넌트를 렌더링한다.
         */
        const $children = document.createDocumentFragment();
        for (const item of this.items ?? []) {
            $children.append(item.$getComponent().getEl());
        }

        this.$getContent().append($children);
        await Promise.all((this.items ?? []).map((child) => Renderer.request(() => child.render(is_force))));
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        /**
         * 스크롤바를 처리한다.
         */
        if (this.$getContent().isConnected() === true) {
            if (this.scrollable !== false) {
                this.getScroller()?.render();
            }
        }

        /**
         * 파일 드래그&드랍을 처리한다.
         */
        if (this.$droppable !== null) {
            this.$droppable.on('dragenter', (_, e: DragEvent) => {
                if (e.dataTransfer?.types.includes('Files') == true) {
                    console.log('dragenter');
                    Components.fileDragDisabled(this);
                }
            });

            this.$droppable.on('dragleave', async (me, e: DragEvent) => {
                if (e.dataTransfer?.types.includes('Files') == true) {
                    if (me.getEl().contains(e.relatedTarget as Node) == false) {
                        console.log('dragleave');
                        this.onFileDrag(true, false);
                        Components.fileDragEnabled();
                    }
                }
            });
        }

        if (this.disabled == true) {
            this.setDisabled(true);
        }
    }

    /**
     * 컴포넌트를 렌더링한다.
     * 중복호출 등을 방지하고, 렌더링이 진행중이더라도 함수를 한번 더 호출하여 대기할 수 있다.
     *
     * @param is_force - 렌더링이 당장 필요없는 상태라도, 강제로 렌더링할지 여부
     */
    readonly render = async (is_force: boolean = false): Promise<void> => {
        /**
         * 렌더링이 완료된 경우 렌더링을 중단한다.
         */
        if (this.isRendered() == true) {
            return;
        }

        /**
         * DOM 연결이 되지 않은 경우, DOM 을 연결하고 컴포넌트의 영역의 크기를 정의한다.
         */
        if (this.connected == false) {
            await this.renderDom();
        }

        /**
         * 렌더링이 필요없는 경우 중단한다.
         */
        if (this.isRenderable() == false && is_force == false) {
            return;
        }

        /**
         * 이미 렌더링이 진행중인 경우, 해당 함수 완료를 기다린다.
         */
        if (this.rendering === null) {
            this.rendering = (async (is_force: boolean): Promise<void> => {
                await this.onBeforeRender();
                if (this.$component === null) return;

                await this.renderContainer(is_force);
                if (this.$component === null) return;

                /**
                 * 렌더링완료 후 설정에 따라 상태를 변경한다.
                 */
                if (this.disabled == true) {
                    this.disable();
                }

                if (this.dragable === true) {
                    this.$component.addClass('dragable');
                }

                this.rendered = true;
                await this.renderState();
                if (this.$component === null) return;

                await this.onRender();
                if (this.$component === null) return;

                if (
                    this.getScroller() !== null ||
                    this.hasEvent('resize') == true ||
                    this.getScrollableParents().length > 0
                ) {
                    this.isObserved = true;
                    Component.observer.observe(this.$component.getEl());
                    this.getScroller()?.updatePosition();
                }
            })(is_force);
        }

        return this.rendering;
    };

    /**
     * 모든 자식 컴포넌트를 제거한다.
     *
     * @param removeDom - 자식 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async removeItems(removeDom: boolean = true): Promise<void> {
        for (let item of this.items ?? []) {
            await item.remove(removeDom);
        }
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        await this.waitForEvents();
        await this.removeItems(false);
        if (removeDom == true && this.$getComponent() !== null) {
            this.$getComponent().remove();
        }

        if (this.isObserved == true && this.$component !== null) {
            Component.observer.unobserve(this.$component.getEl());
        }

        if (this.scroller != null) {
            await this.scroller.remove();
            this.scroller = null;
        }

        /**
         * removeDom 이 false 인 경우, 부모에 의해 제거된다는 의미이므로, 부모에게 알리지 않는다.
         * 직접 컴포넌트가 제거될 경우, 부모에게 제거되었음을 알린다.
         */
        if (removeDom == true) {
            await this.parent?.removeItem(this);
        }

        this.parent = null;
        this.items = [];
        this.rendering = null;
        if (this.$component !== null) {
            (this.$component.getEl() as AuiComponent).component = null;
        }
        this.$component = null;
        this.$container = null;
        this.$content = null;

        Components.remove(this);

        await this.onRemove();
        super.destroy();
    }

    /**
     * destroy() 를 호출할 수 없도록 처리
     */
    destroy(): void {
        Exceptions.warn('destroy() is disabled for Component. Use remove() instead.');
    }

    /**
     * 컴포넌트가 렌더링이 시작되었을 때 이벤트를 처리한다.
     * 렌더링 되기전에 이벤트처리가 완료되기를 기다린다.
     */
    async onBeforeRender(): Promise<void> {
        await this.fireEvent('beforeRender', [this]);
    }

    /**
     * 컴포넌트가 렌더링이 완료되었을 때 이벤트를 처리한다.
     */
    async onRender(): Promise<void> {
        await this.fireEvent('render', [this]);
    }

    /**
     * 컴포넌트가 제거되었을 때 이벤트를 처리한다.
     */
    async onRemove(): Promise<void> {
        await this.fireEvent('remove', [this]);
    }

    /**
     * 자식 컴포넌트가 추가되었을 때 이벤트를 처리한다.
     *
     * @param item - 추가된 자식 컴포넌트
     */
    async onAppendItem(item: Component): Promise<void> {
        await this.fireEvent('appendItem', [item, this]);
    }

    /**
     * 자식 컴포넌트가 제거되었을 때 이벤트를 처리한다.
     *
     * @param item - 제거된 자식 컴포넌트
     */
    async onRemoveItem(item: Component): Promise<void> {
        await this.fireEvent('removeItem', [item, this]);
    }

    /**
     * 파일이 컴포넌트 영역으로 드래그되어 들어왔을 때 이벤트를 처리한다.
     *
     * @param is_droppable - 파일이 컴포넌트에 드롭가능한지 여부
     * @param is_enter - 드래그영역안에 들어왔는지 여부
     */
    async onFileDrag(is_droppable: boolean, is_enter: boolean | null): Promise<void> {
        if (is_droppable == true) {
            this.$droppable.addClass('droppable');
        } else {
            this.$droppable.removeClass('droppable');
        }

        if (is_enter == true) {
            this.$droppable.addClass('dragenter');
        } else if (is_enter !== null) {
            this.$droppable.removeClass('dragenter');
        }
    }

    /**
     * 컴포넌트 DOM 이 연결되었을 때 이벤트를 처리한다.
     */
    async onConnect(): Promise<void> {
        if (this.isShow() == true || Connector.hasStored(this.$getComponent().getEl()) == false) {
            await this.onConnected();
        }
    }

    /**
     * 지연연결 DOM 을 복원하고 connect 이벤트를 발생시킨다.
     */
    async onConnected(): Promise<void> {
        if (Connector.hasTargets(this.$getComponent().getEl()) == true) {
            if (Connector.hasStored(this.$getComponent().getEl()) == true) {
                await Connector.restoreAll(this.$getComponent().getEl());
            }
        }

        if (this.isRendered() == true) {
            const latestScroll = this.getScroller()?.getScrollPosition(true) ?? null;
            if (latestScroll !== null) {
                this.getScroller()?.setPosition(latestScroll.x, latestScroll.y, false);
            }
            this.getScroller()?.preventScroll('x', false);
            this.getScroller()?.preventScroll('y', false);
        }

        this.getScroller()?.updatePosition();
        await this.fireEvent('connect', [this]);
    }

    /**
     * 컴포넌트 DOM 이 연결해제되었을 때 이벤트를 처리한다.
     */
    async onDisconnect(): Promise<void> {
        await this.fireEvent('disconnect', [this]);

        if (this.isRendered() == true) {
            this.getScroller()?.preventScroll('x', true);
            this.getScroller()?.preventScroll('y', true);
        }

        if (Connector.hasTargets(this.$getComponent().getEl()) == true) {
            for (const target of Connector.findTargets(this.$getComponent().getEl())) {
                Connector.store(target);
            }
        }
    }

    /**
     * 키보드 이벤트를 처리한다.
     *
     * @param e - 키보드 이벤트
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onKeyboard(e: KeyboardEvent): Promise<boolean> {
        await this.fireEvent('keyboard', [e, this]);
        return true;
    }

    /**
     * 포커스 이벤트를 처리한다.
     *
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onFocus(): Promise<boolean> {
        await this.fireEvent('focus', [this]);
        return true;
    }

    /**
     * 포커스 해제시 이벤트를 처리한다.
     *
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onBlur(): Promise<boolean> {
        await this.fireEvent('blur', [this]);
        return true;
    }
}

class AuiComponent extends HTMLElement {
    component: Component = null;
    setComponent(component: Component) {
        this.component = component;
    }

    connectedCallback() {
        this.component?.onConnect();
    }

    disconnectedCallback() {
        this.component?.onDisconnect();
    }
}
customElements.define('aui-component', AuiComponent);
